import React from 'react';
import { AbsoluteFill, useCurrentFrame, interpolate, spring, useVideoConfig } from 'remotion';

// Liquid Blob Animation - 液态气泡动画
export const LiquidBlobAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Spring 物理动画
  const scale = spring({
    frame,
    fps,
    config: {
      damping: 200,
      stiffness: 100,
    },
  });

  // 生成液态气泡路径（基于时间变化）
  const generateBlobPath = (time: number, seed: number) => {
    const points: { x: number; y: number }[] = [];
    const numPoints = 8;
    const radius = 150;

    for (let i = 0; i < numPoints; i++) {
      const angle = (i / numPoints) * Math.PI * 2;
      const noise = Math.sin(angle * 2 + time + seed) * 30 + Math.sin(angle * 3 - time * 0.7 + seed) * 20;
      const r = radius + noise;
      points.push({
        x: 540 + Math.cos(angle) * r,
        y: 960 + Math.sin(angle) * r,
      });
    }

    // 使用 Catmull-Rom 样条曲线平滑路径
    let path = `M ${points[0].x} ${points[0].y}`;
    for (let i = 0; i < points.length; i++) {
      const p0 = points[(i - 1 + points.length) % points.length];
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      const p3 = points[(i + 2) % points.length];

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
    }
    path += ' Z';

    return path;
  };

  // 背景气泡动画
  const bgBlobs = Array.from({ length: 5 }, (_, i) => {
    const time = frame * 0.02;
    const seed = i * 1.5;
    const path = generateBlobPath(time, seed);
    const hue = interpolate(i, [0, 4], [200, 280]);
    const opacity = interpolate(i, [0, 4], [0.1, 0.2]);
    const scaleOffset = interpolate(i, [0, 4], [0.8, 1.2]);
    const translateX = Math.sin(frame * 0.01 + i) * 50;
    const translateY = Math.cos(frame * 0.015 + i) * 30;

    return (
      <g
        key={i}
        transform={`translate(${translateX}, ${translateY}) scale(${scale * scaleOffset})`}
        opacity={opacity}
      >
        <path d={path} fill={`hsl(${hue}, 70%, 60%)`} />
      </g>
    );
  });

  // 主气泡动画
  const mainBlobPath = generateBlobPath(frame * 0.03, 0);

  // 小气泡装饰
  const smallBubbles = Array.from({ length: 20 }, (_, i) => {
    const angle = (i / 20) * Math.PI * 2;
    const distance = interpolate(frame, [0, 150], [100, 400]);
    const x = 540 + Math.cos(angle + frame * 0.02) * distance;
    const y = 960 + Math.sin(angle + frame * 0.02) * distance;
    const radius = interpolate(i, [0, 19], [10, 30]);
    const hue = interpolate(i, [0, 19], [180, 300]);

    return (
      <circle
        key={i}
        cx={x}
        cy={y}
        r={radius}
        fill={`hsla(${hue}, 80%, 70%, ${interpolate(i, [0, 19], [0.3, 0.6])})`}
        opacity={interpolate(frame, [0, 30], [0, 1], { extrapolateRight: 'clamp' })}
      />
    );
  });

  // 标题文字
  const titleOpacity = interpolate(frame, [30, 50], [0, 1], { extrapolateRight: 'clamp' });
  const titleY = interpolate(frame, [30, 50], [50, 0], { extrapolateRight: 'clamp' });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      }}
    >
      <svg
        viewBox="0 0 1080 1920"
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        {/* 背景气泡 */}
        {bgBlobs}

        {/* 主气泡 */}
        <g transform={`scale(${scale})`}>
          <path
            d={mainBlobPath}
            fill="url(#blobGradient)"
            filter="url(#glow)"
          />
        </g>

        {/* 小气泡装饰 */}
        {smallBubbles}

        {/* 渐变定义 */}
        <defs>
          <linearGradient id="blobGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ff6b9d" />
            <stop offset="50%" stopColor="#c44ce0" />
            <stop offset="100%" stopColor="#5b7cfa" />
          </linearGradient>

          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="20" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* 标题文字 */}
        <text
          x="540"
          textAnchor="middle"
          fontSize="80"
          fontWeight="bold"
          fill="white"
          opacity={titleOpacity}
          y={960 + titleY}
          style={{ fontFamily: 'Arial, sans-serif' }}
        >
          Liquid Blob
        </text>

        {/* 副标题 */}
        <text
          x="540"
          textAnchor="middle"
          fontSize="40"
          fill="rgba(255, 255, 255, 0.8)"
          opacity={titleOpacity}
          y={1050 + titleY}
          style={{ fontFamily: 'Arial, sans-serif' }}
        >
          液态气泡动画
        </text>
      </svg>

      {/* 背景网格装饰 */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `
            linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          opacity: 0.5,
        }}
      />
    </AbsoluteFill>
  );
};
